<script setup lang="ts">
import {
    CircleCheck,
    FileSearch,
    Globe,
    Link2,
    PenLine,
    RefreshCw,
    ShieldCheck,
} from '@lucide/vue';
import FeatureAvailabilityBadge from '@/components/marketing/shared/FeatureAvailabilityBadge.vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const capabilities = [
    {
        icon: PenLine,
        text: 'Prepare ready-to-review blog articles in your brand voice',
    },
    {
        icon: FileSearch,
        text: 'Suggest titles, meta descriptions and headings',
    },
    {
        icon: Link2,
        text: 'Point out pages that should link to each other',
    },
    {
        icon: RefreshCw,
        text: 'Refresh what it knows when your website changes',
    },
];

const steps = [
    'Connect your website and confirm what it found',
    'Ask for an article, a product update or a page refresh',
    'Approve the draft before anything changes on your site',
];
</script>

<template>
    <section id="website-automation" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                eyebrow="Website automation"
                title="Connect your website."
                description="LaunchStation can prepare content for your own website, not just social channels. You approve every change first."
            />

            <div class="mt-6 flex justify-center">
                <FeatureAvailabilityBadge status="coming-soon" />
            </div>

            <div class="mt-10 grid gap-10 lg:grid-cols-2 lg:items-center">
                <div
                    class="bg-card border-border rounded-2xl border p-6 shadow-sm"
                >
                    <div class="flex items-center gap-3">
                        <span
                            class="bg-secondary/10 text-secondary inline-flex size-10 items-center justify-center rounded-lg"
                        >
                            <Globe class="size-5" aria-hidden="true" />
                        </span>
                        <div>
                            <p class="text-sm font-medium">Your website</p>
                            <p class="text-muted-foreground text-xs">
                                Connected and ready to review drafts
                            </p>
                        </div>
                        <span
                            class="bg-secondary/10 text-muted-foreground ml-auto inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs"
                        >
                            <CircleCheck
                                class="text-secondary size-3"
                                aria-hidden="true"
                            />
                            Connected
                        </span>
                    </div>

                    <ol class="mt-6 grid gap-3">
                        <li
                            v-for="(step, index) in steps"
                            :key="step"
                            class="border-border flex items-start gap-3 rounded-lg border p-3 text-sm"
                        >
                            <span
                                class="bg-muted text-muted-foreground flex size-5 shrink-0 items-center justify-center rounded-full text-xs"
                            >
                                {{ index + 1 }}
                            </span>
                            {{ step }}
                        </li>
                    </ol>

                    <p
                        class="text-muted-foreground mt-4 flex items-center gap-2 text-xs"
                    >
                        <ShieldCheck class="size-3.5" aria-hidden="true" />
                        Higher-risk changes always ask for confirmation first.
                    </p>
                </div>

                <ul class="grid gap-3">
                    <li
                        v-for="item in capabilities"
                        :key="item.text"
                        class="flex items-start gap-3 text-sm sm:text-base"
                    >
                        <component
                            :is="item.icon"
                            class="text-primary mt-0.5 size-4 shrink-0"
                            aria-hidden="true"
                        />
                        {{ item.text }}
                    </li>
                </ul>
            </div>
        </div>
    </section>
</template>
